import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Check, Lightbulb, Paperclip, Upload, type LucideIcon } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { Spinner } from '@/shared/ui/Spinner';
import { Badge, Button, Card } from '@/shared/ui';
import { uploadApi, type UploadType, type PracticeUpload } from '../api/uploadApi';
import { invalidateUploadDependents } from '../utils/invalidateUploadDependents';
import styles from './UploadCard.module.css';

const ACCEPTED_EXTENSIONS = /\.(csv|xlsx|pdf)$/i;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

interface UploadCardProps {
  uploadType: UploadType;
  label: string;
  description: string;
  impact: string;
  icon: LucideIcon;
  hint: string;
  existing?: PracticeUpload | null;
}

export function UploadCard({
  uploadType,
  label,
  description,
  impact,
  icon: Icon,
  hint,
  existing,
}: UploadCardProps) {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // An upload error from this session (bad type, too large, network). A
  // parse failure the server recorded is read from `existing` instead.
  const [error, setError] = useState<string | null>(null);

  const invalidate = useCallback(
    () => invalidateUploadDependents(queryClient, locationId),
    [queryClient, locationId],
  );

  const { mutate: doUpload, isPending } = useMutation({
    mutationFn: (file: File) => uploadApi.upload(locationId, uploadType, file),
    onMutate: () => setError(null),
    onSuccess: invalidate,
    onError: (err: unknown) => {
      setError((err as Error).message || 'Upload failed. Please try again.');
      invalidate();
    },
  });

  const { mutate: doRemove, isPending: removing } = useMutation({
    mutationFn: (id: string) => uploadApi.deleteUpload(id),
    onMutate: () => setError(null),
    onSuccess: invalidate,
    onError: (err: unknown) => setError((err as Error).message || "Couldn't remove the file."),
  });

  const handleFile = useCallback(
    (file: File) => {
      if (!locationId) return;
      // Drag-and-drop bypasses the input's `accept`, so check here too.
      if (!ACCEPTED_EXTENSIONS.test(file.name)) {
        setError('Upload a CSV, XLSX or PDF file. Older .xls files need to be saved as .xlsx.');
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setError('This file is larger than 10 MB. Export a shorter date range and try again.');
        return;
      }
      doUpload(file);
    },
    [locationId, doUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile],
  );

  const uploaded = existing && (existing.status === 'success' || existing.status === 'partial');
  const processing = isPending || existing?.status === 'processing';
  const failureMessage =
    error ??
    (existing?.status === 'failed'
      ? (existing.parseErrors?.[0] ?? "We couldn't read this file. Please try another export.")
      : null);
  // A successful upload can still carry a caveat, e.g. only part of an
  // oversized file was read.
  const uploadWarning = uploaded ? (existing.parseErrors?.[0] ?? null) : null;

  return (
    <Card className={uploaded ? styles.cardUploaded : undefined}>
      <div className={styles.row}>
        <div className={styles.info}>
          <div className={styles.labelRow}>
            <span className={styles.iconAccent}>
              <Icon size={16} strokeWidth={1.75} aria-hidden />
            </span>
            <span className={styles.labelText}>{label}</span>
            {uploaded && (
              <Badge variant="primary">
                <span className={styles.badgeContent}>
                  <Check size={11} aria-hidden /> Uploaded
                </span>
              </Badge>
            )}
          </div>
          <div className={styles.description}>{description}</div>
          <div className={styles.unlocks}>
            <span className={styles.unlocksKey}>UNLOCKS:</span>
            <span className={styles.unlocksVal}>{impact}</span>
          </div>
          {failureMessage && !processing && (
            <div className={styles.errorText} role="alert">
              <AlertCircle size={12} aria-hidden /> {failureMessage}
            </div>
          )}
          {!uploaded && !processing && !failureMessage && (
            <div className={styles.hint}>
              <Lightbulb size={12} aria-hidden /> {hint}
            </div>
          )}
          {uploaded && existing && (
            <div className={styles.fileInfo}>
              <span className={styles.fileInfoName}>
                <Paperclip size={12} aria-hidden /> {existing.fileName}
              </span>
              <span className={styles.fileInfoDot}>·</span>
              <span className={styles.fileInfoSummary}>{existing.summary ?? 'Processed'}</span>
            </div>
          )}
          {uploadWarning && !processing && (
            <div className={styles.warningText} role="status">
              <AlertCircle size={12} aria-hidden /> {uploadWarning}
            </div>
          )}
        </div>

        {processing ? (
          <div className={styles.pending}>
            <Spinner size="sm" /> Analyzing...
          </div>
        ) : !uploaded ? (
          <div
            className={dragOver ? styles.dropZoneActive : styles.dropZone}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <span className={styles.dropIcon}>
              <Upload size={18} strokeWidth={1.75} aria-hidden />
            </span>
            <span className={styles.dropLabel}>Drop file or click</span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,.xlsx,.pdf"
              className={styles.hiddenInput}
              onChange={handleInputChange}
            />
          </div>
        ) : (
          <div className={styles.uploadedActions}>
            <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
              Replace
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.pdf"
                className={styles.hiddenInput}
                onChange={handleInputChange}
              />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => existing && doRemove(existing.id)}
              disabled={removing}
              loading={removing}
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
